import { Translate } from 'next-translate';
import { ParsedUrlQuery } from 'querystring';
import { getLastmessage } from 'services/chatService';
import { generateUserAvatarCodeFromString } from 'services/userService';
import { StorageRequest } from 'types/storageRequest';
import { DEFAULT_AVATAR_CODE } from '../../../constants/dashboard';
import { ConversationMessages } from '../ChatContext/ChatContext.types';
import {
  ChatContact,
  StorageRequestDataInformation,
  StorageRequestStorageProvider,
  StorageRequestUrlPart,
} from './storageRequest.types';

export const getStorageRequestUrlPathPart = (
  isStorageProvider: boolean,
): StorageRequestUrlPart => {
  if (isStorageProvider) {
    return 'receivedStorageRequests';
  }

  return 'sentStorageRequests';
};

export const getChatContactTranslationStatusKey = (
  requestStatus: 'pending' | 'accepted' | 'declined',
  storageRequestType?: StorageRequest['type'],
): string => {
  switch (requestStatus) {
    case 'pending':
      return storageRequestType === 'received' ? 'sentYouARequest' : 'requestPending';

    case 'accepted':
      return 'requestAccepted';

    case 'declined':
      return 'requestDeclined';
  }
};

export const mapChatContacts = (
  storageRequests: StorageRequest[],
  conversationMessages: ConversationMessages | null,
  userId: string | undefined,
  t: Translate,
): ChatContact[] => {
  return storageRequests.reduce((chatContacts, current) => {
    const isAccepted = current.requestStatus === 'accepted';

    if (isAccepted) {
      chatContacts.push({
        logoCidOrAvatar:
          current.type === 'sent'
            ? current.storageProvider.LogoCid || DEFAULT_AVATAR_CODE
            : generateUserAvatarCodeFromString(current.clientName),
        id: current.id,
        name: current.type === 'sent' ? current.storageProvider.name : current.clientName,
        text: getLastmessage(conversationMessages, t, current, userId),
        conversationSid: current.conversationSid,
        type: current.type,
        isSeen: current.isSeen,
        unreadMessagesCount:
          (conversationMessages &&
            conversationMessages[current.conversationSid]?.unreadMessagesCount) ||
          0,
      });
    }

    return chatContacts;
  }, [] as ChatContact[]);
};

export const findStorageRequest = (
  storageRequests: StorageRequest[],
  id: string,
): StorageRequest | undefined =>
  storageRequests.find((storageRequest) => storageRequest.conversationSid === id);

export const getChatPendingStatusTranslationKey = (
  requestStatus: 'pending' | 'accepted' | 'declined',
): string => {
  switch (requestStatus) {
    case 'pending':
      return 'storageRequestPending';

    case 'accepted':
      return 'storageRequestAccepted';

    case 'declined':
      return 'storageRequestDeclined';
  }
};

export const getStorageProviderFromUrl = (query: ParsedUrlQuery): string =>
  (query['storage-provider'] && query['storage-provider'].toString()) || '';

export const mapStorageRequestData = (
  storageRequest: StorageRequest,
): {
  dataInformation: StorageRequestDataInformation;
  storageProvider: StorageRequestStorageProvider;
} => ({
  dataInformation: {
    sizeAndDuration: `${storageRequest.dataAmount} ${storageRequest.dealDuration}`,
    copies: storageRequest.numberOfCopies,
    preferredLocation: storageRequest.preferredLocation,
    purpose: storageRequest.dataCategory,
    bandwidth: storageRequest.clientBandwidth,
  },
  storageProvider: {
    id: storageRequest.storageProvider.id,
    name: storageRequest.storageProvider.name || 'Unknown',
    LogoCid: storageRequest.storageProvider.LogoCid,
  },
});
